import {
  type AdminUser,
  type AdminUserPage,
  CSRF_HEADER,
  type DirectorySourceView,
  type Entity,
  type Role,
  readDomainSignInMode,
} from "@esi/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Search, UserPlus } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ConnectedApps } from "../account/ConnectedApps.tsx";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { formatDate, formatDateTime } from "../i18n/format.ts";
import { t } from "../i18n/i18n.ts";
import { rejectPending } from "../projects/access-requests.ts";
import { useSession } from "../session.tsx";
import { myAccessRequestsKey } from "./AccessRequests.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { EsiCredentialAdmin } from "./EsiCredentialAdmin.tsx";
import { adminKeys, adminRefusal, treeRows, useAdminEntities } from "./entity-tree.ts";
import { type SignInData, useSignInData } from "./SignInAdmin.tsx";

const PAGE = 50;

/** The strongest of a membership's roles, which the role picker shows and sets. */
function mainRole(roles: readonly Role[]): Role {
  return roles.includes("tenant_admin")
    ? "tenant_admin"
    : roles.includes("entity_admin")
      ? "entity_admin"
      : "member";
}

/** `value` once it has stopped changing for `ms`: one search request per pause in typing. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** Sources that provision users (an IdP source only feeds groups at sign-in). */
const syncSources = (user: AdminUser) => user.sources.filter((s) => s.kind !== "idp");

/**
 * The IdPs, by display name, that a "SSO or ESI password" domain is bound to while no active
 * SCIM, push or pull source records subjects for them (#438, design T7): someone removed there
 * keeps their ESI password until removed in ESI. An `idp` source deprovisions nobody. An
 * active source that does not say which IdP it speaks for (an older API) hides every line: the
 * line must never claim no source deprovisions when one may.
 */
export function leaversKeepingPassword(
  data: Pick<SignInData, "identityProviders" | "domains">,
  sources: readonly Pick<DirectorySourceView, "kind" | "status" | "identityProviderId">[],
): string[] {
  const deprovisioning = sources.filter((s) => s.kind !== "idp" && s.status === "active");
  if (deprovisioning.some((s) => s.identityProviderId === undefined)) {
    return [];
  }
  const sourced = new Set(deprovisioning.map((s) => s.identityProviderId));
  const names = new Set<string>();
  for (const d of data.domains) {
    const idp = data.identityProviders.find((p) => p.id === d.identityProviderId);
    if (
      d.verifiedAt !== null &&
      idp?.status === "active" &&
      d.effectiveSignInMode !== undefined &&
      readDomainSignInMode(d.effectiveSignInMode) === "sso_or_esi" &&
      !sourced.has(idp.id)
    ) {
      names.add(idp.displayName);
    }
  }
  return [...names];
}

/** One informational line per such IdP; nothing for someone who cannot read them. */
function LeaversKeepPassword() {
  const { admin } = useAdminApi();
  const { t } = useTranslation();
  const signIn = useSignInData();
  const sources = useQuery({
    // The Directory screen's key and answer.
    queryKey: ["admin", "directory", "sources"],
    queryFn: async (): Promise<DirectorySourceView[]> => ok(await admin.directory.sources.$get()),
    retry: false,
  });
  if (!signIn.data || !sources.data) {
    return null;
  }
  const names = leaversKeepingPassword(signIn.data, sources.data);
  return names.length === 0 ? null : (
    <div className="flex flex-col gap-2">
      {names.map((name) => (
        <Notice key={name} tone="info">
          {t("users.leaversKeepPassword", { name })}
        </Notice>
      ))}
    </div>
  );
}

/**
 * Users in the caller's scope (spec `tenant-administration` "User directory view"): searchable,
 * with status, memberships and roles, the sources that manage them and their last sign-in. A
 * user's dialog changes roles and memberships and disables or re-enables them. Presentation
 * only: the server applies the subtree rule and the lock-out guards.
 */
export function UsersAdmin() {
  const { admin, readOnly } = useAdminApi();
  const { t } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 250);
  const users = useInfiniteQuery({
    queryKey: [...adminKeys.users, q],
    initialPageParam: "",
    queryFn: async ({ pageParam }): Promise<AdminUserPage> =>
      ok(
        await admin.users.$get({
          query: {
            limit: String(PAGE),
            ...(q ? { q } : {}),
            ...(pageParam ? { after: pageParam } : {}),
          },
        }),
      ),
    getNextPageParam: (last) => last.next ?? undefined,
    retry: false,
  });
  const [selected, setSelected] = useState<{ id: string; trigger: HTMLElement | null } | null>(
    null,
  );
  const [inviting, setInviting] = useState(false);
  const forbidden = users.error instanceof ApiError && users.error.status === 403;
  // The IdP list and the directory sources it reads are a tenant admin's.
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  const list = users.data?.pages.flatMap((p) => p.users) ?? [];
  const current = selected ? list.find((u) => u.id === selected.id) : undefined;

  return (
    <div data-esi-content="people" className="flex flex-col gap-8">
      <PageHeader
        title={t("users.title")}
        lead={t("users.lead")}
        actions={
          forbidden || readOnly ? null : (
            <Button onClick={() => setInviting(true)}>
              <UserPlus aria-hidden="true" />
              {t("users.invite")}
            </Button>
          )
        }
      />
      {forbidden || !tenantAdmin ? null : <LeaversKeepPassword />}
      {forbidden ? (
        <Notice>{t("users.adminsOnly")}</Notice>
      ) : (
        <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-3">
          <h2 id={`${id}-heading`} className="sr-only">
            {t("users.title")}
          </h2>
          <div className="relative sm:max-w-sm">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
              strokeWidth={1.75}
            />
            <Input
              type="search"
              aria-label={t("users.search")}
              placeholder={t("users.searchPlaceholder")}
              value={search}
              className="pl-9"
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
          {users.isError ? (
            <Notice tone="danger">{users.error.message}</Notice>
          ) : users.data ? (
            list.length === 0 ? (
              <p className="text-sm text-fg-secondary">
                {q ? t("users.noMatch") : t("users.none")}
              </p>
            ) : (
              <UsersTable
                users={list}
                onOpen={(user, trigger) => setSelected({ id: user.id, trigger })}
              />
            )
          ) : (
            <Skeleton className="h-40 w-full" />
          )}
          {users.hasNextPage ? (
            <Button
              variant="outline"
              className="self-start"
              disabled={users.isFetchingNextPage}
              onClick={() => void users.fetchNextPage()}
            >
              {users.isFetchingNextPage ? t("app.loading") : t("users.more")}
            </Button>
          ) : null}
        </section>
      )}
      {current ? (
        <UserDialog
          user={current}
          returnFocus={selected?.trigger ?? null}
          onClose={() => setSelected(null)}
        />
      ) : null}
      {inviting ? <InviteDialog onClose={() => setInviting(false)} /> : null}
    </div>
  );
}

function StatusLine({ status }: { status: AdminUser["status"] }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <span
        aria-hidden="true"
        className={
          status === "active"
            ? "size-1.5 rounded-full bg-success-fg"
            : "size-1.5 rounded-full bg-fg-muted"
        }
      />
      {t(`users.status.${status}`)}
    </span>
  );
}

function UsersTable(props: {
  users: readonly AdminUser[];
  onOpen: (user: AdminUser, trigger: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
      <table className="w-full min-w-[52rem] text-left text-sm">
        <caption className="sr-only">{t("users.caption")}</caption>
        <thead className="border-b border-line text-xs text-fg-muted">
          <tr>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("users.user")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("users.statusLabel")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("users.memberships")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("users.managedBy")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("users.lastSignIn")}
            </th>
            <th scope="col" className="px-4 py-2">
              <span className="sr-only">{t("users.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {props.users.map((user) => {
            const name = user.displayName ?? user.email;
            const synced = syncSources(user);
            return (
              <tr key={user.id} className="align-top">
                <td className="px-4 py-2.5">
                  <span className="flex flex-col">
                    <span className="font-medium text-fg">{name}</span>
                    {user.displayName ? (
                      <span className="text-xs text-fg-muted">{user.email}</span>
                    ) : null}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="flex flex-col gap-0.5">
                    <StatusLine status={user.status} />
                    {user.status === "active" && user.invitation ? (
                      <span className="text-xs text-fg-muted">
                        {invitationExpired(user.invitation)
                          ? t("users.invitation.expiredShort")
                          : t("users.invitation.pendingShort")}
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-fg-secondary">
                  <ul className="flex flex-col gap-0.5">
                    {user.memberships.map((m) => (
                      <li key={m.entityId}>
                        {m.entityName}
                        <span className="text-fg-muted">
                          {" · "}
                          {t(`account.roles.${mainRole(m.roles)}`)}
                        </span>
                      </li>
                    ))}
                    {user.outsideScope > 0 ? (
                      <li className="text-xs text-fg-muted">
                        {t("users.outsideScope", { count: user.outsideScope })}
                      </li>
                    ) : null}
                  </ul>
                </td>
                <td className="px-4 py-2.5 text-fg-secondary">
                  {synced.length > 0
                    ? synced.map((s) => s.name).join(", ")
                    : t("users.managedByEsi")}
                </td>
                <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                  {user.lastSignInAt ? formatDateTime(user.lastSignInAt) : t("users.neverSignedIn")}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t("users.manageOf", { name })}
                    onClick={(e) => props.onOpen(user, e.currentTarget)}
                  >
                    {t("users.manage")}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function useInvalidateUsers() {
  const queryClient = useQueryClient();
  // A join may have become a pending access request: the requester's list refreshes too.
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: adminKeys.users }),
      queryClient.invalidateQueries({ queryKey: myAccessRequestsKey }),
    ]);
}

/** Roles the caller may give on `entity`: `tenant_admin` on the root only, by tenant admins. */
function roleOptions(entity: Entity | undefined, tenantAdmin: boolean): Role[] {
  return entity?.parentId === null && tenantAdmin
    ? ["member", "entity_admin", "tenant_admin"]
    : ["member", "entity_admin"];
}

function UserDialog(props: {
  user: AdminUser;
  returnFocus: HTMLElement | null;
  onClose: () => void;
}) {
  const { admin, readOnly } = useAdminApi();
  const { user } = props;
  const { t } = useTranslation();
  const { userId: me, csrfToken } = useSession();
  const entities = useAdminEntities();
  const invalidate = useInvalidateUsers();
  const [confirming, setConfirming] = useState(false);
  const name = user.displayName ?? user.email;
  const synced = syncSources(user);
  const byId = new Map((entities.data ?? []).map((e) => [e.id, e]));
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  const status = useMutation({
    mutationFn: async (next: AdminUser["status"]) =>
      ok(
        await admin.users[":userId"].$patch(
          { param: { userId: user.id }, json: { status: next } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => setConfirming(false),
    onSettled: invalidate,
  });
  const onlyMembership = user.memberships.length + user.outsideScope <= 1;

  return (
    <AppDialog
      open
      title={name}
      description={user.displayName ? user.email : undefined}
      returnFocus={props.returnFocus}
      onClose={props.onClose}
    >
      <div className="flex flex-col gap-6">
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
          <dt className="text-fg-muted">{t("users.statusLabel")}</dt>
          <dd>
            <StatusLine status={user.status} />
          </dd>
          <dt className="text-fg-muted">{t("users.lastSignIn")}</dt>
          <dd className="text-fg-secondary tabular-nums">
            {user.lastSignInAt ? formatDateTime(user.lastSignInAt) : t("users.neverSignedIn")}
          </dd>
          {user.invitation ? (
            <>
              <dt className="text-fg-muted">{t("users.invitation.label")}</dt>
              <dd className="text-fg-secondary tabular-nums">
                {invitationExpired(user.invitation)
                  ? t("users.invitation.expired", { date: formatDate(user.invitation.expiresAt) })
                  : t("users.invitation.pending", {
                      date: formatDateTime(user.invitation.expiresAt),
                    })}
              </dd>
            </>
          ) : null}
          <dt className="text-fg-muted">{t("users.managedBy")}</dt>
          <dd className="text-fg-secondary">
            {user.sources.length > 0
              ? user.sources
                  .map((s) => `${s.name} (${t(`directory.kindLabel.${s.kind}`)})`)
                  .join(", ")
              : t("users.managedByEsi")}
          </dd>
        </dl>

        <section aria-labelledby="user-memberships" className="flex flex-col gap-2">
          <h3 id="user-memberships" className="text-sm font-semibold text-fg">
            {t("users.memberships")}
          </h3>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
            {user.memberships.map((m) => (
              <MembershipRow
                key={m.entityId}
                user={user}
                entityId={m.entityId}
                entityName={m.entityName}
                roles={m.roles}
                options={roleOptions(byId.get(m.entityId as never), tenantAdmin)}
                removable={!onlyMembership}
              />
            ))}
          </ul>
          {user.outsideScope > 0 ? (
            <p className="text-xs text-fg-muted">
              {t("users.outsideScope", { count: user.outsideScope })}
            </p>
          ) : null}
          {readOnly ? null : (
            <AddMembership user={user} entities={entities.data ?? []} tenantAdmin={tenantAdmin} />
          )}
        </section>

        {readOnly ? null : (
          <section aria-labelledby="user-access" className="flex flex-col gap-3">
            <h3 id="user-access" className="text-sm font-semibold text-fg">
              {t("users.access")}
            </h3>
            {synced.length > 0 ? (
              <Notice tone="warning">
                {t("users.syncWarning", { sources: synced.map((s) => s.name).join(", ") })}
              </Notice>
            ) : null}
            {!user.manageable ? (
              <Notice>{t("users.notManageable")}</Notice>
            ) : user.id === me ? (
              <p className="text-sm text-fg-secondary">{t("users.selfNote")}</p>
            ) : user.status === "active" ? (
              confirming ? (
                <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
                  <p className="text-sm font-medium text-fg">{t("users.disableTitle", { name })}</p>
                  <ul className="list-disc pl-5 text-sm text-fg-secondary">
                    <li>{t("users.disableEffects.signedOut")}</li>
                    <li>{t("users.disableEffects.kept")}</li>
                    <li>{t("users.disableEffects.reversible")}</li>
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="destructive"
                      disabled={status.isPending}
                      onClick={() => status.mutate("disabled")}
                    >
                      {t("users.disableConfirm")}
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirming(false)}>
                      {t("app.cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="self-start"
                  onClick={() => {
                    status.reset();
                    setConfirming(true);
                  }}
                >
                  {t("users.disable")}
                </Button>
              )
            ) : (
              <Button
                variant="outline"
                className="self-start"
                disabled={status.isPending}
                onClick={() => status.mutate("active")}
              >
                {t("users.enable")}
              </Button>
            )}
            {status.error ? <Notice tone="danger">{adminRefusal(status.error)}</Notice> : null}
          </section>
        )}
        {readOnly || !tenantAdmin || user.status !== "active" || !user.invitation ? null : (
          <InvitationLink user={user} />
        )}
        {readOnly || !tenantAdmin || user.id === me ? null : <EsiCredentialAdmin user={user} />}
        {readOnly || !user.manageable ? null : <UserConnectedApps user={user} />}
        {readOnly || !user.manageable || user.id === me ? null : <MfaReset user={user} />}
        {readOnly || !user.manageable || user.status !== "disabled" ? null : (
          <MemoryErasure user={user} />
        )}
      </div>
    </AppDialog>
  );
}

function MembershipRow(props: {
  user: AdminUser;
  entityId: string;
  entityName: string;
  roles: readonly Role[];
  options: Role[];
  removable: boolean;
}) {
  const { admin, readOnly } = useAdminApi();
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const invalidate = useInvalidateUsers();
  const current = mainRole(props.roles);
  const param = { entityId: props.entityId, userId: props.user.id };
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const setRole = useMutation({
    mutationFn: async (role: Role) =>
      ok(
        await admin.entities[":entityId"].members[":userId"].roles.$put(
          { param, json: { roles: [role] } },
          headers,
        ),
      ),
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await admin.entities[":entityId"].members[":userId"].$delete({ param }, headers);
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSettled: invalidate,
  });
  const options = props.options.includes(current) ? props.options : [current, ...props.options];
  const error = setRole.error ?? remove.error;
  if (readOnly) {
    return (
      <li className="flex flex-wrap items-center gap-2 px-3 py-2.5 text-sm">
        <span className="min-w-0 flex-1 truncate text-fg">{props.entityName}</span>
        <span className="text-fg-secondary">{t(`account.roles.${current}`)}</span>
      </li>
    );
  }
  return (
    <li className="flex flex-col gap-2 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm text-fg">{props.entityName}</span>
        <NativeSelect
          aria-label={t("users.roleIn", { entity: props.entityName })}
          value={current}
          disabled={setRole.isPending}
          className="w-44"
          onChange={(e) => {
            remove.reset();
            setRole.mutate(e.currentTarget.value as Role);
          }}
        >
          {options.map((role) => (
            <option key={role} value={role}>
              {t(`account.roles.${role}`)}
            </option>
          ))}
        </NativeSelect>
        {props.removable ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("users.removeFrom", { entity: props.entityName })}
            disabled={remove.isPending}
            onClick={() => {
              setRole.reset();
              remove.mutate();
            }}
          >
            {t("app.remove")}
          </Button>
        ) : null}
      </div>
      {error ? <InlineError>{adminRefusal(error)}</InlineError> : null}
    </li>
  );
}

function AddMembership(props: {
  user: AdminUser;
  entities: readonly Entity[];
  tenantAdmin: boolean;
}) {
  const { admin } = useAdminApi();
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const invalidate = useInvalidateUsers();
  const taken = new Set(props.user.memberships.map((m) => m.entityId as string));
  const rows = treeRows(props.entities).filter((r) => !taken.has(r.entity.id));
  const [entityId, setEntityId] = useState("");
  const [role, setRole] = useState<Role>("member");
  const add = useMutation({
    mutationFn: async () =>
      ok(
        // Joining an entity may open projects to the admin: then it waits for their approval.
        rejectPending(
          await admin.entities[":entityId"].members.$post(
            { param: { entityId }, json: { email: props.user.email, roles: [role] } },
            { headers: { [CSRF_HEADER]: csrfToken } },
          ),
        ),
      ),
    onSuccess: () => setEntityId(""),
    onSettled: invalidate,
  });
  if (rows.length === 0) {
    return null;
  }
  const entity = props.entities.find((e) => e.id === entityId);
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (entityId && !add.isPending) {
          add.mutate();
        }
      }}
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-48 flex-1 flex-col gap-1.5">
          <Label htmlFor={`${id}-entity`} className="text-xs text-fg-muted">
            {t("users.addTo")}
          </Label>
          <NativeSelect
            id={`${id}-entity`}
            value={entityId}
            onChange={(e) => {
              add.reset();
              setEntityId(e.currentTarget.value);
              setRole("member");
            }}
          >
            <option value="">{t("users.pickEntity")}</option>
            {rows.map((r) => (
              <option key={r.entity.id} value={r.entity.id}>
                {`${" ".repeat(r.depth)}${r.entity.name}`}
              </option>
            ))}
          </NativeSelect>
        </div>
        <NativeSelect
          aria-label={t("users.role")}
          value={role}
          className="w-44"
          onChange={(e) => setRole(e.currentTarget.value as Role)}
        >
          {roleOptions(entity, props.tenantAdmin).map((r) => (
            <option key={r} value={r}>
              {t(`account.roles.${r}`)}
            </option>
          ))}
        </NativeSelect>
        <Button type="submit" variant="outline" disabled={!entityId || add.isPending}>
          {t("users.add")}
        </Button>
      </div>
      {add.error ? <InlineError>{adminRefusal(add.error)}</InlineError> : null}
    </form>
  );
}

/**
 * "Invite": adds a member by e-mail through the add-member API. Unknown e-mails get an account
 * (and, outside federated domains, an invitation); the invitation flow itself is the server's.
 */
function InviteDialog(props: { onClose: () => void }) {
  const { admin } = useAdminApi();
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const entities = useAdminEntities();
  const invalidate = useInvalidateUsers();
  const rows = treeRows(entities.data ?? []);
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [entityId, setEntityId] = useState("");
  const [role, setRole] = useState<Role>("member");
  const target = entityId || rows[0]?.entity.id || "";
  const invite = useMutation({
    mutationFn: async () =>
      ok(
        // Joining an entity may open projects to the admin: then it waits for their approval.
        rejectPending(
          await admin.entities[":entityId"].members.$post(
            {
              param: { entityId: target },
              json: {
                email: email.trim(),
                roles: [role],
                ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
              },
            },
            { headers: { [CSRF_HEADER]: csrfToken } },
          ),
        ),
      ),
    onSuccess: async (added) => {
      await invalidate();
      // The account exists but its email was refused (dev-ses-email): the admin hands it over.
      if ("user" in added && added.invitation?.sent === false) {
        setUnsent({ id: added.user.id, email: added.user.email });
      } else {
        props.onClose();
      }
    },
  });
  const [unsent, setUnsent] = useState<{ id: string; email: string } | null>(null);
  if (unsent) {
    return (
      <AppDialog open title={t("users.inviteTitle")} onClose={props.onClose}>
        <div className="flex flex-col gap-4">
          <Notice tone="warning" live>
            <span className="flex flex-col gap-0.5">
              <span className="font-medium">{t("users.invitation.unsentTitle")}</span>
              <span className="[overflow-wrap:anywhere]">
                {tenantAdmin
                  ? t("users.invitation.unsentBody", { email: unsent.email })
                  : t("users.invitation.unsentAskAdmin", { email: unsent.email })}
              </span>
            </span>
          </Notice>
          {tenantAdmin ? <InvitationLink user={unsent as Pick<AdminUser, "id" | "email">} /> : null}
          <div className="flex justify-end">
            <Button variant="outline" onClick={props.onClose}>
              {t("users.invitation.close")}
            </Button>
          </div>
        </div>
      </AppDialog>
    );
  }
  return (
    <AppDialog
      open
      title={t("users.inviteTitle")}
      description={t("users.inviteBody")}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim() && target && !invite.isPending) {
            invite.mutate();
          }
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-email`}>{t("users.email")}</Label>
          <Input
            id={`${id}-email`}
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => {
              invite.reset();
              setEmail(e.currentTarget.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-name`}>{t("users.displayName")}</Label>
          <Input
            id={`${id}-name`}
            maxLength={200}
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-entity`}>{t("users.entity")}</Label>
            <NativeSelect
              id={`${id}-entity`}
              value={target}
              onChange={(e) => {
                setEntityId(e.currentTarget.value);
                setRole("member");
              }}
            >
              {rows.map((r) => (
                <option key={r.entity.id} value={r.entity.id}>
                  {`${" ".repeat(r.depth)}${r.entity.name}`}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-role`}>{t("users.role")}</Label>
            <NativeSelect
              id={`${id}-role`}
              value={role}
              onChange={(e) => setRole(e.currentTarget.value as Role)}
            >
              {roleOptions(
                entities.data?.find((e) => e.id === target),
                tenantAdmin,
              ).map((r) => (
                <option key={r} value={r}>
                  {t(`account.roles.${r}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        {invite.error ? <Notice tone="danger">{adminRefusal(invite.error)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={!email.trim() || !target || invite.isPending}>
            {invite.isPending ? t("users.inviting") : t("users.inviteConfirm")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

/**
 * The user's connected native and MCP clients, revocable by the admin (native-device-and-cimd);
 * hidden under a support grant, whose access stops before the user's grants.
 */
function UserConnectedApps({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  return (
    <section aria-labelledby={`${id}-apps`} className="flex flex-col gap-3">
      <h3 id={`${id}-apps`} className="text-sm font-semibold text-fg">
        {t("connectedApps.title")}
      </h3>
      <ConnectedApps
        queryKey={["admin", "users", user.id, "connected-apps"]}
        load={async () =>
          ok(await admin.users[":userId"]["connected-apps"].$get({ param: { userId: user.id } }))
        }
        revoke={async (grantId) => {
          const res = await admin.users[":userId"]["connected-apps"][":grantId"].$delete(
            { param: { userId: user.id, grantId } },
            { headers: { [CSRF_HEADER]: csrfToken } },
          );
          if (!res.ok) {
            throw await apiError(res);
          }
        }}
        empty={t("connectedApps.emptyOf", { name: user.displayName ?? user.email })}
      />
    </section>
  );
}

/**
 * Removes the user's second factors (a lost phone): they enrol again at their next sign-in where
 * two-step verification is required. A reason is asked and kept in the audit log.
 */
const invitationExpired = (invitation: { expiresAt: string }) =>
  Date.parse(invitation.expiresAt) <= Date.now();

/**
 * "Copy invite link" (spec "Copy invite link in the users screen"): tenant admins create a fresh
 * link for a pending invitation, shown once. It replaces the emailed one, so the admin confirms
 * first; nothing is emailed.
 */
function InvitationLink({ user }: { user: Pick<AdminUser, "id" | "email"> }) {
  const { t } = useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const issue = useMutation({
    mutationFn: async () =>
      ok(
        await admin.users[":userId"]["invitation-link"].$post(
          { param: { userId: user.id } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      setConfirming(false);
      setCopied(false);
    },
    // The listing shows the new expiry (or that the invitation is gone).
    onSettled: () => queryClient.invalidateQueries({ queryKey: adminKeys.users }),
    // The link is a credential: it leaves the mutation cache as soon as the panel closes.
    gcTime: 0,
  });
  const issued = issue.data;
  return (
    <section aria-labelledby={`${id}-invitation`} className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h3 id={`${id}-invitation`} className="text-sm font-semibold text-fg">
          {t("users.invitation.title")}
        </h3>
        <p className="text-sm text-fg-secondary">
          {t("users.invitation.lead", { email: user.email })}
        </p>
      </div>
      {issued ? (
        <div
          role="status"
          aria-labelledby={`${id}-issued`}
          className="flex flex-col gap-3 rounded-lg border border-info-border/50 bg-info-bg px-4 py-3.5"
        >
          <div className="flex flex-col gap-0.5">
            <p
              id={`${id}-issued`}
              className="text-sm font-semibold text-fg [overflow-wrap:anywhere]"
            >
              {t("users.invitation.issuedTitle", { email: user.email })}
            </p>
            <p className="text-sm text-fg-secondary">{t("users.invitation.issuedBody")}</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex min-w-0 flex-1 basis-64 flex-col gap-1.5">
              <Label htmlFor={`${id}-link`}>{t("users.invitation.field")}</Label>
              <Input
                id={`${id}-link`}
                readOnly
                value={issued.link}
                onFocus={(e) => e.currentTarget.select()}
                className="bg-surface-raised font-mono text-[13px]"
              />
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(issued.link);
                  setCopied(true);
                } catch {
                  // No clipboard (permissions, insecure context): the field stays selectable.
                }
              }}
            >
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? t("users.invitation.copied") : t("users.invitation.copyLink")}
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-fg-secondary tabular-nums">
              {t("users.invitation.expires", { date: formatDateTime(issued.expiresAt) })}
            </p>
            <Button size="sm" onClick={() => issue.reset()}>
              {t("users.invitation.done")}
            </Button>
          </div>
        </div>
      ) : confirming ? (
        <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
          <p className="text-sm font-medium text-fg">{t("users.invitation.confirmTitle")}</p>
          <ul className="list-disc pl-5 text-sm text-fg-secondary">
            <li>{t("users.invitation.replaces")}</li>
            <li className="[overflow-wrap:anywhere]">
              {t("users.invitation.once", { email: user.email })}
            </li>
          </ul>
          {issue.error ? <InlineError>{invitationRefusal(issue.error, user)}</InlineError> : null}
          <div className="flex flex-wrap gap-2">
            <Button disabled={issue.isPending} onClick={() => issue.mutate()}>
              {issue.isPending ? t("users.invitation.creating") : t("users.invitation.create")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              {t("app.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          className="self-start"
          onClick={() => {
            issue.reset();
            setConfirming(true);
          }}
        >
          <Copy aria-hidden="true" />
          {t("users.invitation.copy")}
        </Button>
      )}
    </section>
  );
}

function invitationRefusal(error: Error, user: Pick<AdminUser, "email">): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "invitation_not_pending":
        return t("users.invitation.refused.notPending", { email: user.email });
      case "user_disabled":
        return t("users.invitation.refused.disabled");
      case "federated_account":
        return t("users.invitation.refused.federated");
      case "throttled":
        return t("users.invitation.refused.throttled");
      case "account_missing":
        return t("users.invitation.refused.missing");
    }
    if (error.status === 403) {
      return t("users.invitation.refused.forbidden");
    }
    if (error.status === 404) {
      return t("users.invitation.refused.notFound");
    }
  }
  return adminRefusal(error);
}

function MfaReset({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const reset = useMutation({
    mutationFn: async () => {
      const res = await admin.users[":userId"]["mfa-reset"].$post(
        { param: { userId: user.id }, json: { reason: reason.trim() } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: () => {
      setOpen(false);
      setReason("");
    },
  });
  return (
    <section aria-labelledby={`${id}-mfa`} className="flex flex-col gap-3">
      <h3 id={`${id}-mfa`} className="text-sm font-semibold text-fg">
        {t("users.mfa.title")}
      </h3>
      {reset.isSuccess ? (
        <Notice tone="success" live>
          {t("users.mfa.done")}
        </Notice>
      ) : null}
      {open ? (
        <form
          className="flex flex-col gap-3 rounded-lg border border-line p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim() && !reset.isPending) {
              reset.mutate();
            }
          }}
        >
          <p className="text-sm text-fg-secondary">{t("users.mfa.effect")}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-reason`}>{t("users.mfa.reason")}</Label>
            <Textarea
              id={`${id}-reason`}
              required
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </div>
          {reset.error ? <InlineError>{mfaRefusal(reset.error)}</InlineError> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              variant="destructive"
              disabled={!reason.trim() || reset.isPending}
            >
              {t("users.mfa.confirm")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t("app.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="outline"
          className="self-start"
          onClick={() => {
            reset.reset();
            setOpen(true);
          }}
        >
          {t("users.mfa.reset")}
        </Button>
      )}
    </section>
  );
}

/**
 * Erasing a disabled user's memories (spec "Memory erasure from the user view", memory-governance):
 * the count first, then the effects and a typed confirmation of their e-mail. The erase call
 * carries the count back; a different one is refused and the new count shown. Never a memory's
 * text: the admin sees counts only.
 */
function MemoryErasure({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const countKey = ["admin", "users", user.id, "memories"] as const;
  const count = useQuery({
    queryKey: countKey,
    enabled: open,
    queryFn: async () =>
      ok(await admin.users[":userId"].memories.count.$get({ param: { userId: user.id } })),
    retry: false,
  });
  const erase = useMutation({
    mutationFn: async (expected: number) =>
      ok(
        await admin.users[":userId"].memories.erase.$post(
          { param: { userId: user.id }, json: { expected } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      setOpen(false);
      setTyped("");
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "memory_count_changed") {
        void queryClient.invalidateQueries({ queryKey: countKey });
      }
    },
  });
  const confirmed = typed.trim().toLowerCase() === user.email.toLowerCase();
  const total = count.data?.count;
  return (
    <section aria-labelledby={`${id}-memory`} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 id={`${id}-memory`} className="text-sm font-semibold text-fg">
          {t("users.memory.title")}
        </h3>
        <p className="text-sm text-fg-secondary">{t("users.memory.lead")}</p>
      </div>
      {erase.isSuccess ? (
        <Notice tone="success" live>
          {t("users.memory.done", { count: erase.data.memories })}
          {erase.data.projectMemoriesKept > 0
            ? ` ${t("users.memory.kept", { count: erase.data.projectMemoriesKept })}`
            : null}
        </Notice>
      ) : null}
      {open ? (
        <form
          className="flex flex-col gap-3 rounded-lg border border-line p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmed && total !== undefined && !erase.isPending) {
              erase.mutate(total);
            }
          }}
        >
          {count.isPending ? (
            <p className="text-sm text-fg-muted">{t("users.memory.counting")}</p>
          ) : count.isError ? (
            <Notice tone="danger">{memoryRefusal(count.error)}</Notice>
          ) : (
            <>
              {/* None left: pending suggestions and their authorship may still be erased. */}
              {total === 0 ? (
                <p className="text-sm text-fg-secondary">{t("users.memory.none")}</p>
              ) : (
                <p className="text-sm font-medium text-fg tabular-nums" data-testid="memory-count">
                  {t("users.memory.count", { count: total })}
                </p>
              )}
              <ul className="list-disc pl-5 text-sm text-fg-secondary">
                <li>{t("users.memory.effects.deleted")}</li>
                <li>{t("users.memory.effects.projects")}</li>
                <li>{t("users.memory.effects.backups")}</li>
              </ul>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${id}-confirm`}>
                  {t("users.memory.confirmLabel", { email: user.email })}
                </Label>
                <Input
                  id={`${id}-confirm`}
                  autoComplete="off"
                  spellCheck={false}
                  value={typed}
                  onChange={(e) => setTyped(e.currentTarget.value)}
                />
              </div>
            </>
          )}
          {erase.error ? <InlineError>{memoryRefusal(erase.error)}</InlineError> : null}
          <div className="flex flex-wrap gap-2">
            {total !== undefined ? (
              <Button type="submit" variant="destructive" disabled={!confirmed || erase.isPending}>
                {total === 0
                  ? t("users.memory.erase")
                  : t("users.memory.confirm", { count: total })}
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t("app.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="outline"
          className="self-start"
          onClick={() => {
            erase.reset();
            setTyped("");
            // A fresh count every time: an old one would only be refused (409).
            queryClient.removeQueries({ queryKey: countKey });
            setOpen(true);
          }}
        >
          {t("users.memory.erase")}
        </Button>
      )}
    </section>
  );
}

function memoryRefusal(error: Error): string {
  if (error instanceof ApiError) {
    if (error.code === "memory_count_changed") {
      return t("users.memory.changed");
    }
    if (error.code === "memory_user_active") {
      return t("users.memory.active");
    }
  }
  return adminRefusal(error);
}

function mfaRefusal(error: Error): string {
  if (error instanceof ApiError) {
    if (error.code === "self_mfa_reset") {
      return t("users.mfa.self");
    }
    if (error.status === 422) {
      return t("users.mfa.federated");
    }
  }
  return adminRefusal(error);
}
